import type { DateRange } from '@/stores/filters'
import {
  differenceInDays,
  endOfDay,
  isWithinInterval,
  startOfDay,
  subDays,
  subMonths,
} from 'date-fns'
import Papa from 'papaparse'

export interface CyclabilityCitiesData {
  date: Date
  freeFloatingVehicles: number
  stationBasedVehicles: number
  freeFloatingTrips: number
  stationBasedTrips: number
  counter: number
}

interface ParsedCyclabilityData {
  day: string
  nb_vehic_avail_ff: number
  nb_vehic_avail_sb: number
  nb_trip_ff: number
  nb_trip_sb: number
  counter: number
}

function getNumberOfMonths(limit: number) {
  return Math.floor(limit / 30)
}

function hasEnoughData(compareWith: 'lastPrevious' | 'lastYear', data: CyclabilityCitiesData[], rangeLimit: number) {
  if (compareWith === 'lastPrevious') {
    return (data.length ?? 0) > rangeLimit * 2
  }

  if (compareWith === 'lastYear') {
    return (data.length ?? 0) > (rangeLimit + 365)
  }

  return false
}

function getLastDateRange(dateRange: DateRange) {
  const { start, end } = dateRange
  const nbrDays = differenceInDays(end, start)

  return {
    start: startOfDay(subDays(start, nbrDays + 1)),
    end: endOfDay(subDays(start, 1)),
  }
}

function getLastYearDateRange(dateRange: DateRange) {
  const { start, end } = dateRange

  return {
    start: startOfDay(subMonths(start, 12)),
    end: endOfDay(subMonths(end, 12)),
  }
}

function useCyclabilityCitiesDataCsv(subfix?: string) {
  const { slug } = storeToRefs(useCityStore())
  const cityDataPath = computed(() => `/data/cyclability_data_${slug.value}${subfix ? `_${subfix}` : ''}.csv`)

  // Fetch and parse the CSV data
  const { data, isFetching, execute } = useFetch<CyclabilityCitiesData[]>(cityDataPath, {
    afterFetch: (context) => {
      const parsed = Papa.parse<ParsedCyclabilityData>(context.data, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
      })

      context.data = parsed.data.map((row) => {
        return {
          date: new Date(row.day),
          freeFloatingVehicles: row.nb_vehic_avail_ff,
          stationBasedVehicles: row.nb_vehic_avail_sb,
          freeFloatingTrips: row.nb_trip_ff,
          stationBasedTrips: row.nb_trip_sb,
          counter: row.counter,
        }
      })

      return context
    },
    immediate: false,
  })

  onMounted(() => {
    watch(slug, async (slug, oldSlug) => {
      if (slug && slug !== oldSlug) {
        await execute()
      }
    }, { immediate: true })
  })

  function getDataInRange(dateRange: DateRange) {
    return data.value?.filter((item) => {
      return isWithinInterval(item.date, { start: dateRange.start, end: dateRange.end })
    }) || []
  }

  return {
    data,
    loading: isFetching,
    city: slug,
    getDataInRange,
  }
}

interface CyclabilityCitiesDataOptions {
  compareWith?: 'lastPrevious' | 'lastYear' | null
  rangeLimit?: -1 | 30 | 60 | 90 | 120 | 180 | 365
  subfix?: string
  enforceDateRange?: DateRange
}

export function useCyclabilityCitiesData(options: CyclabilityCitiesDataOptions = {}) {
  const { subfix, enforceDateRange, compareWith = null, rangeLimit = -1 } = options
  const { data, loading, city, getDataInRange } = useCyclabilityCitiesDataCsv(subfix)

  const cityDateRange = ref<DateRange>(enforceDateRange || defaultDateRange())
  const cityDateRangeLimit = ref<DateRange>(enforceDateRange || defaultDateRange())
  const dataInRange = ref<CyclabilityCitiesData[]>([])
  const lastDateRange = ref<DateRange>()
  const comparedDataInRange = ref<CyclabilityCitiesData[] | null>(compareWith ? null : [])

  const hasLimit = rangeLimit > 0

  watch(data, (data) => {
    if (enforceDateRange) {
      cityDateRange.value = enforceDateRange
      cityDateRangeLimit.value = enforceDateRange
      return
    }

    if (data) {
      const end = data[data.length - 1].date

      cityDateRange.value = {
        start: hasLimit && compareWith && hasEnoughData(compareWith, data, rangeLimit) ? startOfDay(subMonths(end, getNumberOfMonths(rangeLimit))) : data[0].date,
        end,
      }

      cityDateRangeLimit.value = {
        start: data[0].date,
        end,
      }
    }
  })

  watch(cityDateRange, (cityDateRange) => {
    if (!cityDateRange) {
      return
    }

    dataInRange.value = getDataInRange(cityDateRange)

    if (!compareWith || !hasLimit || !hasEnoughData(compareWith, data.value ?? [], rangeLimit)) {
      return
    }

    if (compareWith === 'lastPrevious') {
      // use the same previous period of the data to compare with
      lastDateRange.value = getLastDateRange(cityDateRange)
    } else if (compareWith === 'lastYear') {
      // use the last year same period of the data to compare with
      lastDateRange.value = getLastYearDateRange(cityDateRange)
    }

    if (lastDateRange.value) {
      comparedDataInRange.value = getDataInRange(lastDateRange.value)
    }
  }, { immediate: true, deep: true })

  return {
    cityDateRange,
    cityDateRangeLimit,
    lastDateRange,
    data,
    dataInRange,
    comparedDataInRange,
    loading,
    city,
    getDataInRange,
    getLastDateRange,
  }
}
